import React, { useState, useEffect } from 'react';
import {
  Stack,
  Card,
  Text,
  Title,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Badge,
  Alert,
  Box,
  Modal,
  PinInput,
  useMantineColorScheme,
  SimpleGrid,
  Switch,
  Divider,
} from '@mantine/core';
import {
  IconMail,
  IconPhone,
  IconLock,
  IconKey,
  IconInfoCircle,
  IconEye,
  IconBell,
  IconDeviceFloppy,
  IconRosetteDiscountCheckFilled,
} from '@tabler/icons-react';
import { getFlatRoundedInputStyles, flatRoundedInputProps } from '../../styles/inputStyles';
import { notifications } from '@mantine/notifications';
import { 
  perfilUsuarioService,
  ProfileDto
} from '../../services/perfilUsuarioService';

// Interfaces temporales hasta implementar endpoints de seguridad
interface ConfiguracionPrivacidadRequest {
  recibirNotificaciones?: boolean;
  recibirNewsletters?: boolean;
  perfilPublico?: boolean;
  mostrarEmail?: boolean;
  mostrarTelefono?: boolean;
  mostrarUniversidad?: boolean;
  permitirMensajes?: boolean;
  mostrarEstadisticas?: boolean;
}

const PrivacySecurityContent: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [guardandoPrivacidad, setGuardandoPrivacidad] = useState<boolean>(false);

  // Inputs compactos y planos (mismo estilo que Información Personal)
  const inputProps = { ...flatRoundedInputProps, size: 'sm' as const };

  // Estados para datos del usuario
  const [perfil, setPerfil] = useState<ProfileDto | null>(null);
  const [configuracionPrivacidad, setConfiguracionPrivacidad] = useState<ConfiguracionPrivacidadRequest>({
    recibirNotificaciones: true,
    recibirNewsletters: false,
    perfilPublico: true,
    mostrarEmail: false,
    mostrarTelefono: false,
    mostrarUniversidad: true,
    permitirMensajes: true,
    mostrarEstadisticas: true,
  });

  // Estados para cambio de email
  const [emailModalOpen, setEmailModalOpen] = useState<boolean>(false);
  const [nuevoEmail, setNuevoEmail] = useState<string>('');
  const [contrasenaParaEmail, setContrasenaParaEmail] = useState<string>('');

  // Estados para cambio de contraseña
  const [passwordModalOpen, setPasswordModalOpen] = useState<boolean>(false);
  const [contrasenaActual, setContrasenaActual] = useState<string>('');
  const [nuevaContrasena, setNuevaContrasena] = useState<string>('');
  const [confirmarNuevaContrasena, setConfirmarNuevaContrasena] = useState<string>('');

  // Estados para verificación de teléfono
  const [phoneVerificationModalOpen, setPhoneVerificationModalOpen] = useState<boolean>(false);
  const [codigoVerificacion, setCodigoVerificacion] = useState<string>('');

  // Cargar datos iniciales
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const perfilData = await perfilUsuarioService.getMiPerfil();

        setPerfil(perfilData);
        
        // Mapear configuración de privacidad desde el perfil
        setConfiguracionPrivacidad({
          recibirNotificaciones: perfilData.recibirNotificaciones,
          recibirNewsletters: perfilData.recibirNewsletters,
          perfilPublico: perfilData.perfilPublico,
          mostrarEmail: perfilData.privacy.mostrarEmail,
          mostrarTelefono: perfilData.privacy.mostrarTelefono,
          mostrarUniversidad: perfilData.privacy.mostrarUniversidad,
          permitirMensajes: perfilData.privacy.permitirMensajes,
          mostrarEstadisticas: perfilData.privacy.mostrarEstadisticas,
        });

      } catch (error) {
        console.error('Error loading privacy data:', error);
        notifications.show({
          color: 'red',
          title: 'Error',
          message: 'No se pudo cargar la configuración de privacidad',
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);


  // Cambiar email (temporal - necesita endpoint)
  const handleChangeEmail = async () => {
    notifications.show({
      color: 'orange',
      title: 'Función en desarrollo',
      message: 'La funcionalidad de cambio de email está en desarrollo',
    });
    setEmailModalOpen(false);
  };

  // Cambiar contraseña
  const handleChangePassword = async () => {
    if (!contrasenaActual || !nuevaContrasena || !confirmarNuevaContrasena) {
      notifications.show({
        color: 'red',
        title: 'Campos incompletos',
        message: 'Completa todos los campos para continuar',
      });
      return;
    }
    if (nuevaContrasena.length < 8) {
      notifications.show({
        color: 'red',
        title: 'Contraseña muy corta',
        message: 'La nueva contraseña debe tener al menos 8 caracteres',
      });
      return;
    }
    if (nuevaContrasena !== confirmarNuevaContrasena) {
      notifications.show({
        color: 'red',
        title: 'Las contraseñas no coinciden',
        message: 'La nueva contraseña y su confirmación deben ser iguales',
      });
      return;
    }
    if (nuevaContrasena === contrasenaActual) {
      notifications.show({
        color: 'red',
        title: 'Contraseña inválida',
        message: 'La nueva contraseña debe ser distinta de la actual',
      });
      return;
    }

    try {
      setSaving(true);
      await perfilUsuarioService.cambiarMiContrasena(contrasenaActual, nuevaContrasena);
      notifications.show({
        color: 'green',
        title: 'Contraseña actualizada',
        message: 'Tu contraseña se cambió correctamente',
      });
      setContrasenaActual('');
      setNuevaContrasena('');
      setConfirmarNuevaContrasena('');
      setPasswordModalOpen(false);
    } catch (error: any) {
      const backendMessage = error?.response?.data?.message;
      notifications.show({
        color: 'red',
        title: 'No se pudo cambiar la contraseña',
        message: backendMessage || 'Verifica que tu contraseña actual sea correcta',
      });
    } finally {
      setSaving(false);
    }
  };

  // Funciones de verificación (temporales - necesitan endpoints)
  const handleRequestEmailVerification = async () => {
    notifications.show({
      color: 'orange',
      title: 'Función en desarrollo',
      message: 'La verificación de email está en desarrollo',
    });
  };

  const handleRequestPhoneVerification = async () => {
    notifications.show({
      color: 'orange',
      title: 'Función en desarrollo',
      message: 'La verificación de teléfono está en desarrollo',
    });
  };

  const handleVerifyPhoneCode = async () => {
    notifications.show({
      color: 'orange',
      title: 'Función en desarrollo',
      message: 'La verificación de código está en desarrollo',
    });
    setPhoneVerificationModalOpen(false);
  };

  // Cambia un toggle de la configuración de privacidad/notificaciones
  const setToggle = (key: keyof ConfiguracionPrivacidadRequest, value: boolean) => {
    setConfiguracionPrivacidad((prev) => ({ ...prev, [key]: value }));
  };

  // Guarda la configuración de privacidad y notificaciones
  const handleGuardarPrivacidad = async () => {
    try {
      setGuardandoPrivacidad(true);
      const actualizado = await perfilUsuarioService.actualizarPerfil({
        recibirNotificaciones: configuracionPrivacidad.recibirNotificaciones,
        recibirNewsletters: configuracionPrivacidad.recibirNewsletters,
        perfilPublico: configuracionPrivacidad.perfilPublico,
        privacy: {
          mostrarEmail: configuracionPrivacidad.mostrarEmail,
          mostrarTelefono: configuracionPrivacidad.mostrarTelefono,
          mostrarUniversidad: configuracionPrivacidad.mostrarUniversidad,
          permitirMensajes: configuracionPrivacidad.permitirMensajes,
          mostrarEstadisticas: configuracionPrivacidad.mostrarEstadisticas,
        },
      });
      setPerfil(actualizado);
      notifications.show({
        color: 'green',
        title: 'Cambios guardados',
        message: 'Tu configuración de privacidad se actualizó correctamente',
      });
    } catch (error) {
      console.error('Error guardando privacidad:', error);
      notifications.show({
        color: 'red',
        title: 'No se pudo guardar',
        message: 'Ocurrió un error al actualizar tu configuración de privacidad',
      });
    } finally {
      setGuardandoPrivacidad(false);
    }
  };

  if (loading) {
    return (
      <Box p="xl">
        <Text>Cargando configuración de privacidad...</Text>
      </Box>
    );
  }

  const cardStyle = {
    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
  };

const textColor = colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26';
const mutedColor = colorScheme === 'dark' ? '#94a3b8' : '#5a5550';
const smallLabelStyle: React.CSSProperties = {
  fontSize: 12,
  color: mutedColor as string,
  fontFamily: 'Inter, sans-serif',
  textAlign: 'left',
  marginBottom: 4,
  display: 'inline-flex',
  whiteSpace: 'nowrap',
  alignItems: 'center'
};
const L = (text: string) => (<span style={smallLabelStyle}>{text}</span>);

// Fila de switch para la configuración de privacidad / notificaciones
const toggleRow = (
  key: keyof ConfiguracionPrivacidadRequest,
  label: string,
  desc: string,
  disabled = false,
) => (
  <Group key={key} justify="space-between" align="center" wrap="nowrap" gap="md">
    <Box style={{ flex: 1, minWidth: 0 }}>
      <Text size="sm" fw={500} ta="left" style={{ color: textColor }}>{label}</Text>
      <Text size="xs" ta="left" style={{ color: mutedColor }}>{desc}</Text>
    </Box>
    <Switch
      checked={!!configuracionPrivacidad[key]}
      onChange={(e) => setToggle(key, e.currentTarget.checked)}
      disabled={disabled || guardandoPrivacidad}
      size="md"
      color="#BEC5A4"
      styles={{ track: { cursor: 'pointer' } }}
    />
  </Group>
);

const perfilPublico = !!configuracionPrivacidad.perfilPublico;

return (
    <Card withBorder radius="lg" p="lg" style={cardStyle}>
      <Stack gap="md">
        <Box>
          <Title order={2} size="h3" ta="left" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif', color: textColor }}>
            Privacidad y Seguridad
          </Title>
          <Text size="sm" ta="left" style={{ color: mutedColor }}>
            Administra tus datos de acceso y qué información es visible para otros usuarios
          </Text>
        </Box>

        <Group gap="xs" align="center">
          <IconLock size={16} style={{ color: mutedColor }} />
          <Text fw={600} size="sm" style={{ color: textColor }}>Seguridad de la cuenta</Text>
        </Group>

<SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {/* Email */}
          <Card withBorder radius="lg" p="md" style={cardStyle}>
            <Stack gap="sm" align="flex-start">
              <Group justify="space-between" align="center" w="100%">
                <Group gap="sm">
                  <IconMail size={18} style={{ color: mutedColor }} />
                  <Text fw={600} size="sm" ta="left" style={{ color: textColor }}>Email</Text>
                </Group>
                {perfil?.emailVerificado ? (
                  <IconRosetteDiscountCheckFilled size={20} color="#1d9bf0" aria-label="Verificado" />
                ) : (
                  <Badge size="sm" color="orange" variant="filled">SIN VERIFICAR</Badge>
                )}
              </Group>
              <Text size="sm" ta="left" style={{ color: mutedColor }}>
                {perfil?.email}
              </Text>
              <Group justify="flex-start" gap="xs">
                <Button size="sm" color="dark" radius="md" onClick={() => setEmailModalOpen(true)}>
                  Cambiar
                </Button>
                {!perfil?.emailVerificado && (
                  <Button size="sm" variant="light" color="orange" radius="md" onClick={handleRequestEmailVerification} loading={saving}>
                    Verificar
                  </Button>
                )}
              </Group>
            </Stack>
          </Card>

          {/* Teléfono */}
          <Card withBorder radius="lg" p="md" style={cardStyle}>
            <Stack gap="sm" align="flex-start">
              <Group gap="sm">
                <IconPhone size={18} style={{ color: mutedColor }} />
                <Text fw={600} size="sm" ta="left" style={{ color: textColor }}>Teléfono</Text>
              </Group>
              <Text size="sm" ta="left" style={{ color: mutedColor }}>
                {perfil?.telefono}
              </Text>
            </Stack>
          </Card>

          {/* Contraseña */}
          <Card withBorder radius="lg" p="md" style={cardStyle}>
            <Stack gap="sm" align="flex-start">
              <Group gap="sm">
                <IconLock size={18} style={{ color: mutedColor }} />
                <Text fw={600} size="sm" ta="left" style={{ color: textColor }}>Contraseña</Text>
              </Group>
              <Text size="sm" ta="left" style={{ color: mutedColor }}>
                Última actualización: {perfil?.ultimaActividad ? new Date(perfil.ultimaActividad).toLocaleDateString() : '13/10/2025'}
              </Text>
              <Group justify="flex-start">
                <Button size="sm" color="dark" radius="md" onClick={() => setPasswordModalOpen(true)}>
                  Cambiar
                </Button>
              </Group>
            </Stack>
          </Card>
        </SimpleGrid>

        <Divider my="xs" color={colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} />

        {/* Privacidad del perfil */}
        <Group gap="xs" align="center">
          <IconEye size={16} style={{ color: mutedColor }} />
          <Text fw={600} size="sm" style={{ color: textColor }}>Privacidad del perfil</Text>
        </Group>

        <Card withBorder radius="lg" p="md" style={cardStyle}>
          <Stack gap="md">
            {toggleRow(
              'perfilPublico',
              'Perfil público',
              'Permite que otros usuarios encuentren y vean tu perfil',
            )}
            <Divider color={colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'} />
            {!perfilPublico && (
              <Text size="xs" ta="left" style={{ color: mutedColor, fontStyle: 'italic' }}>
                Activa “Perfil público” para configurar qué información se muestra.
              </Text>
            )}
            {toggleRow('mostrarUniversidad', 'Mostrar universidad', 'Tu universidad será visible en tu perfil', !perfilPublico)}
            {toggleRow('mostrarEmail', 'Mostrar correo', 'Tu correo será visible en tu perfil', !perfilPublico)}
            {toggleRow('mostrarTelefono', 'Mostrar teléfono', 'Tu teléfono será visible en tu perfil', !perfilPublico)}
            {toggleRow('mostrarEstadisticas', 'Mostrar estadísticas', 'Otros podrán ver tu rendimiento y posición en el ranking', !perfilPublico)}
            {toggleRow('permitirMensajes', 'Permitir mensajes', 'Otros usuarios podrán enviarte mensajes', !perfilPublico)}
          </Stack>
        </Card>

        {/* Notificaciones */}
        <Group gap="xs" align="center">
          <IconBell size={16} style={{ color: mutedColor }} />
          <Text fw={600} size="sm" style={{ color: textColor }}>Notificaciones</Text>
        </Group>

        <Card withBorder radius="lg" p="md" style={cardStyle}>
          <Stack gap="md">
            {toggleRow('recibirNotificaciones', 'Notificaciones de la plataforma', 'Avisos sobre tu actividad, exámenes y recordatorios')}
            {toggleRow('recibirNewsletters', 'Boletines y novedades', 'Recibe correos con noticias y consejos de estudio')}
          </Stack>
        </Card>

        <Group justify="flex-start">
          <Button
            color="dark"
            radius="md"
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleGuardarPrivacidad}
            loading={guardandoPrivacidad}
          >
            Guardar cambios
          </Button>
        </Group>


        {/* Modal para cambio de email */}
        <Modal
          opened={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          title="Cambiar Email"
          centered
        >
          <Stack gap="md">
            <Alert
              icon={<IconInfoCircle size={16} />}
              color="blue"
              variant="light"
            >
              Se enviará un enlace de verificación al nuevo email para confirmar el cambio.
            </Alert>

<TextInput
              label={L('Nuevo email')}
              placeholder="nuevo-email@ejemplo.com"
              type="email"
              value={nuevoEmail}
              onChange={(e) => setNuevoEmail(e.target.value)}
              required
              {...inputProps}
              styles={getFlatRoundedInputStyles(colorScheme)}
            />

<PasswordInput
              label={L('Contraseña actual')}
              placeholder="Tu contraseña actual"
              value={contrasenaParaEmail}
              onChange={(e) => setContrasenaParaEmail(e.target.value)}
              required
              {...inputProps}
              styles={getFlatRoundedInputStyles(colorScheme)}
            />

<Group justify="flex-end" mt="md">
              <Button variant="outline" radius="xl" onClick={() => setEmailModalOpen(false)}>
                Cancelar
              </Button>
              <Button radius="xl" onClick={handleChangeEmail} loading={saving}>
                Cambiar Email
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Modal para cambio de contraseña */}
        <Modal
          opened={passwordModalOpen}
          onClose={() => setPasswordModalOpen(false)}
          title="Cambiar Contraseña"
          centered
        >
          <Stack gap="md">
            <Alert
              icon={<IconKey size={16} />}
              color="yellow"
              variant="light"
            >
              Tu nueva contraseña debe tener al menos 8 caracteres y contener una mezcla de letras, números y símbolos.
            </Alert>

<PasswordInput
              label={L('Contraseña actual')}
              placeholder="Tu contraseña actual"
              value={contrasenaActual}
              onChange={(e) => setContrasenaActual(e.target.value)}
              required
              {...inputProps}
              styles={getFlatRoundedInputStyles(colorScheme)}
            />

<PasswordInput
              label={L('Nueva contraseña')}
              placeholder="Nueva contraseña"
              value={nuevaContrasena}
              onChange={(e) => setNuevaContrasena(e.target.value)}
              required
              {...inputProps}
              styles={getFlatRoundedInputStyles(colorScheme)}
            />

<PasswordInput
              label={L('Confirmar nueva contraseña')}
              placeholder="Confirma la nueva contraseña"
              value={confirmarNuevaContrasena}
              onChange={(e) => setConfirmarNuevaContrasena(e.target.value)}
              required
              {...inputProps}
              styles={getFlatRoundedInputStyles(colorScheme)}
            />

<Group justify="flex-end" mt="md">
              <Button variant="outline" radius="xl" onClick={() => setPasswordModalOpen(false)}>
                Cancelar
              </Button>
              <Button radius="xl" onClick={handleChangePassword} loading={saving}>
                Cambiar Contraseña
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Modal para verificación de teléfono */}
        <Modal
          opened={phoneVerificationModalOpen}
          onClose={() => setPhoneVerificationModalOpen(false)}
          title="Verificar Teléfono"
          centered
        >
          <Stack gap="md">
            <Alert
              icon={<IconPhone size={16} />}
              color="blue"
              variant="light"
            >
              Ingresa el código de 6 dígitos que enviamos a tu teléfono {perfil?.telefono}
            </Alert>

            <Box style={{ display: 'flex', justifyContent: 'center' }}>
              <PinInput
                length={6}
                size="lg"
                value={codigoVerificacion}
                onChange={setCodigoVerificacion}
              />
            </Box>

            <Group justify="space-between" mt="md">
<Button 
                variant="subtle" 
                radius="xl"
                onClick={handleRequestPhoneVerification}
                size="sm"
              >
                Reenviar código
              </Button>
<Group gap="sm">
                <Button variant="outline" radius="xl" onClick={() => setPhoneVerificationModalOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  radius="xl"
                  onClick={handleVerifyPhoneCode} 
                  disabled={codigoVerificacion.length < 6}
                >
                  Verificar
                </Button>
              </Group>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Card>
  );
};

export default PrivacySecurityContent;